import Box from '@mui/material/Box'
import type { SxProps, Theme } from '@mui/material/styles'
import TableSortLabel from '@mui/material/TableSortLabel'
import type { ReactElement } from 'react'

import KestrixCheckbox from '../KestrixCheckbox/KestrixCheckbox.tsx'
import { propertyTableHeaderTokens } from './property-table-header.tokens.ts'
import type { PropertyTableHeaderFieldProps } from './property-table-header.types.ts'
import {
  headerContentRowSx,
  PROPERTY_TABLE_HEADER_SORT_TEST_ID,
  PropertyTableHeaderTextContent
} from './PropertyTableHeaderTextContent.tsx'
import { toMuiSortDirection } from './to-mui-sort-direction.ts'

const sortLabelSx: SxProps<Theme> = {
  color: propertyTableHeaderTokens.text,
  '&.Mui-active': { color: propertyTableHeaderTokens.text },
  '&:hover': { color: propertyTableHeaderTokens.text },
  '& .MuiTableSortLabel-icon': { display: 'none' }
}

const renderTextHeaderField = (
  props: Extract<PropertyTableHeaderFieldProps, { type: 'text' }>
): ReactElement => {
  const {
    label,
    align = 'left',
    showPrimaryDot = false,
    tooltip,
    tooltipAriaLabel,
    sortField,
    activeSortField,
    sortDirection = 'asc',
    onSort
  } = props

  const textContent = (
    <PropertyTableHeaderTextContent
      label={label}
      align={align}
      showPrimaryDot={showPrimaryDot}
      tooltip={tooltip}
      tooltipAriaLabel={tooltipAriaLabel}
    />
  )

  if (sortField == null) {
    return textContent
  }

  const isActive = activeSortField === sortField

  return (
    <TableSortLabel
      data-testid={PROPERTY_TABLE_HEADER_SORT_TEST_ID}
      data-sort-active={isActive ? 'true' : 'false'}
      active={isActive}
      direction={toMuiSortDirection(sortDirection)}
      onClick={() => onSort?.(sortField)}
      hideSortIcon
      sx={{
        ...sortLabelSx,
        flexDirection: align === 'center' ? 'column' : 'row',
        alignItems: align === 'center' ? 'center' : 'flex-start',
        minWidth: 0,
        width: '100%'
      }}
    >
      {textContent}
    </TableSortLabel>
  )
}

/**
 * Renders a property table header cell's inner content.
 *
 * Text fields support an optional tooltip, primary dot, and sort control.
 * Checkbox fields render {@link KestrixCheckbox} only.
 */
export const PropertyTableHeaderField = (
  props: PropertyTableHeaderFieldProps
): ReactElement => {
  switch (props.type) {
    case 'checkbox': {
      const {
        checkboxChecked = false,
        checkboxIndeterminate = false,
        checkboxDisabled = false,
        onCheckboxChange,
        checkboxAriaLabel = 'Select row'
      } = props

      return (
        <Box sx={headerContentRowSx('center')}>
          <KestrixCheckbox
            data-testid="property-table-header-checkbox"
            checked={checkboxChecked}
            indeterminate={checkboxIndeterminate}
            disabled={checkboxDisabled}
            onChange={onCheckboxChange}
            inputProps={{ 'aria-label': checkboxAriaLabel }}
          />
        </Box>
      )
    }
    case 'text':
      return renderTextHeaderField(props)
  }
}
